import { createAction, PayloadAction } from '@reduxjs/toolkit';
import { Persistor } from 'redux-persist';
import { adobeTargetDataLayer } from '@buy-viasat/utils';
import { DataLayerEventName, EventLogParams, TrackingActions, TrackingCategory } from '../analytics';
import { RESET_STATE_ACTION } from '../types';
import { createSlice } from '../utils';
import { Routes } from '../navigator';
import {
  AppState,
  Country,
  CreditCheck,
  CustomerType,
  ErrorModalOverride,
  Features,
  initialAppState,
  Modals,
  ScriptId,
} from './types';

export const appSlice = createSlice({
  name: 'app',
  initialState: initialAppState,
  reducers: {
    analyticsEvent(
      state: AppState,
      action: PayloadAction<{ category: TrackingCategory; action: TrackingActions; params?: EventLogParams }>,
    ) {
      // no-op reducer — used to trigger saga
    },
    loadScript(state: AppState, action: PayloadAction<{ src: string; id: ScriptId }>): void {
      // no-op reducer — used to trigger saga
    },
    loadFeatureFlags(state: AppState, action: PayloadAction<Partial<Features>>): void {
      state.loadedFeatureFlags = true;
      const flags = { ...state.featureFlags, ...action.payload };
      state.featureFlags = flags;

      const stringifiedFlags = Object.fromEntries(Object.entries(flags).map(([key, value]) => [key, String(value)]));
      adobeTargetDataLayer.push({
        event: DataLayerEventName.AA_EVENT,
        eventData: {
          event: 'featureFlagsLoaded',
          featureFlags: stringifiedFlags,
        },
      });
    },
    setErrorRetryAction(
      state: AppState,
      action: PayloadAction<{ type: string; payload: Record<string, unknown> | [] | null }>,
    ): void {
      state.errorRetryAction = action.payload;
    },
    retrieveFeatureFlags(state: AppState): void {
      // no-op reducer — used to trigger saga
    },
    setCidPhoneMap(state: AppState): void {
      // no-op reducer — used to trigger saga
    },
    loadCidPhoneMap(state: AppState, action: PayloadAction<{ phoneNumber: string; campaignId?: string }>): void {
      state.loadedCidPhoneMap = true;
      state.cidPhoneNumber = action.payload.phoneNumber;
      state.cidPhoneNumberCampaignId = action.payload.campaignId;
    },
    setMarketPreferences(): void {
      // no-op reducer — used to trigger saga
    },
    setCurrentRoute(state: AppState, action: PayloadAction<Routes>): void {
      state.currentRoute = action.payload;
    },
    scriptLoadedSuccessfully(state: AppState, action: PayloadAction<ScriptId>): void {
      // no-op reducer — used to trigger saga
    },
    // need to pass the persistor to the saga through the payload
    startSession(state: AppState, action: PayloadAction<Persistor>) {
      // no-op reducer — used to trigger saga
    },
    setBillingContactEqualsAccountHolder(state: AppState): void {
      state.billingContactEqualsAccountHolder = true;
    },
    setBillingContactNotEqualAccountHolder(state: AppState): void {
      state.billingContactEqualsAccountHolder = false;
    },
    setOnSiteContactEqualsAccountHolder(state: AppState): void {
      state.onSiteContactEqualsAccountHolder = true;
    },
    setOnSiteContactNotEqualAccountHolder(state: AppState): void {
      state.onSiteContactEqualsAccountHolder = false;
    },
    setCreditCheckResults(state: AppState, action: PayloadAction<CreditCheck[]>): void {
      state.creditCheckResults = action.payload;
    },
    setLocale(state: AppState, action: PayloadAction<string>): void {
      state.locale = action.payload;
    },
    setCustomerType(state: AppState, action: PayloadAction<CustomerType>): void {
      state.customerType = action.payload;
    },
    setCountry(state: AppState, action: PayloadAction<Country>): void {
      state.country = action.payload;
    },
    setIsAvailable(state: AppState, action: PayloadAction<boolean>): void {
      state.isAvailable = action.payload;
    },
    setIsCartVisible(state: AppState, action: PayloadAction<boolean>): void {
      state.isCartVisible = action.payload;
    },
    setIsCheckoutButtonDisabled(state: AppState, action: PayloadAction<boolean>): void {
      state.isCheckoutButtonDisabled = action.payload;
    },
    setIsModalVisible(state: AppState, action: PayloadAction<boolean>): void {
      state.isModalVisible = action.payload;
    },
    setIsOrderSubmitting(state: AppState, action: PayloadAction<boolean>): void {
      state.isOrderSubmitting = action.payload;
    },
    setIsPaymentInformationComplete(state: AppState, action: PayloadAction<boolean>): void {
      state.isPaymentInformationComplete = action.payload;
    },
    setIsPersonalInformationComplete(state: AppState, action: PayloadAction<boolean>): void {
      state.isPersonalInformationComplete = action.payload;
    },
    setIsScheduleInstallationComplete(state: AppState, action: PayloadAction<boolean>): void {
      state.isScheduleInstallationComplete = action.payload;
    },
    setIsTaxCodesLoading(state: AppState, action: PayloadAction<boolean>): void {
      state.isTaxCodesLoading = action.payload;
    },
    setLocation(state: AppState, action: PayloadAction<string | undefined>): void {
      state.location = action.payload;
    },
    setModalVisible(state: AppState, action: PayloadAction<Modals>): void {
      state.isModalVisible = true;
      state.currentModal = action.payload;
    },
    setModalVisibleOverride(state: AppState, action: PayloadAction<ErrorModalOverride>): void {
      state.isModalVisible = true;
      state.currentModal = action.payload.modal;
      state.errorModalOverrideBody = action.payload.overrideBody;
      state.errorModalOverrideHeader = action.payload.overrideHeader;
      state.errorModalOverrideButtonText = action.payload.overrideButtonText;
      state.errorModalOverrideSvg = action.payload.overrideSvg;
    },
    setPartyId(state: AppState, action: PayloadAction<string>): void {
      state.partyId = action.payload;
    },
    setOrderId(state: AppState, action: PayloadAction<string>): void {
      state.orderId = action.payload;
    },
    setRelnId(state: AppState, action: PayloadAction<string>): void {
      state.relnId = action.payload;
    },
    setPartyAndRelnId(
      state: AppState,
      { payload: { partyId, relnId } }: PayloadAction<{ partyId: string; relnId: string }>,
    ): void {
      state.partyId = partyId;
      state.relnId = relnId;
    },
    setOrganizationPartyId(state: AppState, action: PayloadAction<string>): void {
      state.organizationPartyId = action.payload;
    },
    setRedirected(state: AppState, action: PayloadAction<boolean>): void {
      state.redirected = action.payload;
    },
    setServiceabilityError(state: AppState, action: PayloadAction<boolean>): void {
      state.serviceabilityError = action.payload;
    },
    setServiceabilityMaybe(state: AppState, action: PayloadAction<boolean>): void {
      state.serviceabilityMaybe = action.payload;
    },
    setStartTime(state: AppState, action: PayloadAction<Date>): void {
      state.startTime = action.payload.toString();
    },
    toggleIsModalVisible(state: AppState): void {
      state.isModalVisible = !state.isModalVisible;
    },
    setExternalWorkOrderId(state: AppState, action: PayloadAction<string>): void {
      state.workOrderId = action.payload;
    },
    setHasSubmittedScheduleInstall(state: AppState, action: PayloadAction<boolean>): void {
      state.hasSubmittedScheduleInstall = action.payload;
    },
    setSalesAgreementId(state: AppState, action: PayloadAction<string>): void {
      state.salesAgreementId = action.payload;
    },
    setFulfillmentAgreementId(state: AppState, action: PayloadAction<string>): void {
      state.fulfillmentAgreementId = action.payload;
    },
    setFeatureFlag(state: AppState, action: PayloadAction<Partial<Features>>): void {
      state.featureFlags = { ...state.featureFlags, ...action.payload };
    },
    setErrorModalAttempts(state: AppState, action: PayloadAction<number>) {
      state.errorRetryAttempts = action.payload;
    },
    setSmbLinkResidentialRedirect(state: AppState, action: PayloadAction<boolean>) {
      state.smbLinkResidentialRedirect = action.payload;
    },
    setSmbLinkPlansRedirect(state: AppState, action: PayloadAction<boolean>) {
      state.smbLinkPlansRedirect = action.payload;
    },
    setDealerIdParam(state: AppState, action: PayloadAction<string>) {
      state.urlParams.dealerId = action.payload;
    },
    setPartnerIdParam(state: AppState, action: PayloadAction<string>) {
      state.urlParams.partnerId = action.payload;
    },
    setAffiliateIdParam(state: AppState, action: PayloadAction<string>) {
      state.urlParams.affiliateId = action.payload;
    },
    setSessionIdParam(state: AppState, action: PayloadAction<string>) {
      state.urlParams.sessionId = action.payload;
    },
    setCampaignIdParam(state: AppState, action: PayloadAction<string>) {
      state.urlParams.campaignId = action.payload;
    },
    setOfferIdParam(state: AppState, action: PayloadAction<string>) {
      state.urlParams.offerId = action.payload;
    },
    resetPartyId(state: AppState): void {
      state.partyId = '';
    },
    setIsEmbedded(state: AppState, action: PayloadAction<boolean>): void {
      state.isEmbedded = action.payload;
    },
    setIsStandAlone(state: AppState, action: PayloadAction<boolean>): void {
      state.isStandAlone = action.payload;
    },
    setToggleMfeCTA(state: AppState, action: PayloadAction<boolean>): void {
      state.hideMfeCTA = action.payload;
    },
    setDisplayCalculatedLaterCartLabel(state: AppState, action: PayloadAction<boolean>): void {
      state.displayCalculatedLaterCartLabel = action.payload;
    },
    resetSession(state: AppState) {
      // no-op reducer — used to trigger saga
    },
    logURLParams(state: AppState) {
      // no-op reducer — used to trigger saga
    },
    logURL(state: AppState) {
      // no-op reducer — used to trigger saga
    },
    // EVENT HANDLERS //
    onModalCancel(state: AppState, action: PayloadAction<Modals>): void {
      // no-op reducer — used to trigger saga
    },
    onModalConfirm(state: AppState, action: PayloadAction<Modals>): void {
      // no-op reducer — used to trigger saga
    },
    // EVENT HANDLERS END //
  },

  extraReducers: (builder) => {
    builder.addCase(resetState, (state: AppState): AppState => ({
      ...initialAppState,
      locale: state.locale,
      country: state.country,
      customerType: state.customerType,
      featureFlagsUserKey: state.featureFlagsUserKey,
    }));
  },
});

const resetState = createAction(RESET_STATE_ACTION.type);

export const { actions: appActions, reducer: appReducer, name: appKey } = appSlice;
